import React, { useState } from "react";
import { Copy, Check, Mail, MessageSquare, Share2 } from "lucide-react";
import { PRICE_FROM } from "../../seo/siteMeta";
import { track } from "../../seo/track";
import { shareUrl } from "../../utils/referral";

// The one message a signed-in filer shares: where they file, linking to the home page.
// Kept to a single message so sharing after a filing is one tap, not a choice. The free
// calculators and guides are shared from their own public pages (ShareRow). Paths are
// canonical (trailing slash - see SeoHead's absUrl).
export const SHARE_MESSAGES = [
  {
    id: "filing",
    label: "Where I file",
    text: `I e-file my Form 2290 with EasyFile2290 - from $${PRICE_FROM}, and VIN corrections are free:`,
    path: "/",
  },
];

// FTC 16 CFR 255.5: a referral reward is a material connection the reader wouldn't expect,
// so it travels with every message that carries the filer's referral code.
const DISCLOSURE = "(I get a filing credit if you file through my link.)";

const CHANNEL_IDS = ["whatsapp", "facebook", "x", "linkedin", "email", "sms"];
// Share dialogs that take only a URL - no room for the referral disclosure.
const URL_ONLY_CHANNELS = new Set(["facebook", "linkedin"]);

function channelLinks(text, url) {
  const full = `${text} ${url}`;
  const enc = encodeURIComponent;
  return [
    { id: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${enc(full)}` },
    { id: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { id: "x", label: "X", href: `https://x.com/intent/post?text=${enc(text)}&url=${enc(url)}` },
    { id: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { id: "email", label: "Email", href: `mailto:?subject=${enc("Form 2290")}&body=${enc(full)}`, Icon: Mail },
    { id: "sms", label: "Text", href: `sms:?&body=${enc(full)}`, Icon: MessageSquare },
  ];
}

/**
 * Share box. Two shapes:
 *  - in the app (messages omitted): the filer shares SHARE_MESSAGES' one message; with `refCode`
 *    every link carries ?ref= and the message carries the reward disclosure.
 *  - on a public page: pass `messages={[{ id, text, path }]}` with just that page.
 * Facebook and LinkedIn only take a URL (their share dialogs ignore preset text), so the
 * disclosure can't ride along there - those buttons are hidden while a refCode is active
 * (FTC: don't encourage endorsements through features that can't carry the disclosure).
 */
export default function ShareBox({
  refCode = null,
  messages = SHARE_MESSAGES,
  title = "Share EasyFile2290 with your group",
  intro = "Know other truckers, a dispatcher group, or an association chat? Share where you file.",
  placement,
  compact = false,
}) {
  const [selectedId, setSelectedId] = useState(messages[0].id);
  const [copied, setCopied] = useState(false);
  const selected = messages.find((m) => m.id === selectedId) || messages[0];

  const build = (medium) => {
    const url = shareUrl(selected.path, { code: refCode, medium });
    const text = refCode ? `${selected.text} ${DISCLOSURE}` : selected.text;
    return { url, text };
  };

  const onShare = (channel) => track("share_click", { channel, message: selected.id, placement, referral: !!refCode });

  const copy = async () => {
    const { url, text } = build("copy");
    onShare("copy");
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const nativeShare = async () => {
    const { url, text } = build("native");
    onShare("native");
    try {
      await navigator.share({ text, url });
    } catch {
      /* the filer closed the share sheet */
    }
  };

  // Each channel gets its own utm_medium, so GA shows which button sent the visit.
  const links = CHANNEL_IDS.filter((id) => !(refCode && URL_ONLY_CHANNELS.has(id))).map((id) => {
    const { url, text } = build(id);
    return channelLinks(text, url).find((c) => c.id === id);
  });

  const btn =
    "inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[var(--line-2)] bg-[var(--panel)] text-[13px] font-semibold text-[var(--ink)] no-underline hover:border-[var(--accent)] transition-colors";

  return (
    <div className={compact ? "" : "rounded-[16px] border border-[var(--line)] bg-[var(--panel)] p-5 text-left"}>
      {title && <div className="text-[15px] font-bold text-[var(--ink)] mb-1">{title}</div>}
      {intro && <p className="text-[13px] text-[var(--muted)] m-0 mb-3">{intro}</p>}

      {messages.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3" role="radiogroup" aria-label="Message to share">
          {messages.map((m) => (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={m.id === selected.id}
              onClick={() => setSelectedId(m.id)}
              className={`h-8 px-3 rounded-full text-[12px] font-semibold border transition-colors ${
                m.id === selected.id
                  ? "bg-[var(--accent-soft)] border-[var(--accent)] text-[var(--accent)]"
                  : "bg-[var(--panel-2)] border-[var(--line-2)] text-[var(--muted)]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-[10px] bg-[var(--panel-2)] border border-[var(--line-2)] px-3 py-2.5 text-[13px] text-[var(--ink)] mb-3 break-words">
        {build("copy").text} <span className="text-[var(--accent)]">{build("copy").url}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={copy} className={btn}>
          {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy message"}
        </button>
        {canNativeShare && (
          <button type="button" onClick={nativeShare} className={btn}>
            <Share2 size={15} /> Share…
          </button>
        )}
        {links.map(({ id, label, href, Icon }) => (
          <a
            key={id}
            href={href}
            target={id === "email" || id === "sms" ? undefined : "_blank"}
            rel="noopener noreferrer"
            onClick={() => onShare(id)}
            className={btn}
          >
            {Icon && <Icon size={15} />} {label}
          </a>
        ))}
      </div>

      {refCode && (
        <p className="text-[12px] text-[var(--muted)] mt-3 mb-0">
          Your link includes your referral code. When a friend's first return is accepted by the IRS, you get a
          filing credit - the message says so, as the FTC requires for rewarded recommendations.
        </p>
      )}
    </div>
  );
}
